import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { connect } from 'react-redux';
import BillToPartySelect from './components/BillToPartySelect';
import BillToPartyInput from './components/BillToPartyInput';
import { checkVendor } from 'actions/mdm';
import './styles/BillToPartyComponent.css';
import { costAndRevErrorMessage } from 'views/job-costing/utils/models';
import { OTHER_DROPDOWN_OPTION, FIELD_NAMES, SEARCH_STATUS, PARTNER_TYPES } from './constants';
import { TypePartyDetails, IOptions } from './interfaces';
interface IMapDispatchToProps {
    searchBillToPartyAction: (code: { beCode?: string; factCode?: string; }) => Promise<any>;
}
interface IProps {
    isAdding: boolean;
    billToParty: string;
    onUpdate: (targetField: string, evt: string, label?: string) => void;
    billToPartners: IOptions[];
    billToPartnersRawData: any[];
    billToCustomers: IOptions[];
    billToCustomersRawData: any[];
    searchBillToPartyAction: (code: { beCode?: string; factCode?: string; }) => Promise<any>;
    errorList: string[];
    isDisabled: boolean;
    athenaBillToParty?: any;
    showLabel?: boolean;
}

const BillToPartyComponent = (props: IProps) => {
    const {
        isAdding,
        billToParty,
        onUpdate,
        billToPartners,
        billToPartnersRawData,
        billToCustomers,
        billToCustomersRawData,
        searchBillToPartyAction,
        errorList,
        isDisabled,
        athenaBillToParty,
        showLabel = true,
    } = props;

    const [isOthers, setOthers] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchBillToPartyResult, setSearchBillToPartyResult] = useState('');
    const [searchHasError, setSearchHasError] = useState(false);
    const [searchErrorMessage, setSearchErrorMessage] = useState('');
    const [searchResultStatus, setSearchResultsStatus] = useState('');

    const partnerMap = useMemo(() => new Map(billToPartnersRawData?.map(p => [p.factCode, p])), [billToPartnersRawData]);
    const customerMap = useMemo(() => new Map(billToCustomersRawData?.map(c => [c.customerCode, c])), [billToCustomersRawData]);
    const billToPartyError = useMemo(() => Array.isArray(errorList) && errorList.includes(FIELD_NAMES.PARTY_CODE), [errorList]);
    const icbIndicatorError = useMemo(() => Array.isArray(errorList) && errorList.includes(FIELD_NAMES.ICB), [errorList]);

    useEffect(() => {
        if (!isOthers) {
            if (billToPartyError) {
                setHasError(true);
                setErrorMessage(costAndRevErrorMessage.SIMPLE_ERROR);
                return;
            }
            if (!isAdding && icbIndicatorError) {
                setHasError(true);
                setErrorMessage(costAndRevErrorMessage.validationMsgCostandRev);
                return;
            }
            setHasError(false);
            setErrorMessage('');
            setSearchHasError(false);
            setSearchErrorMessage('');
        } else if (billToPartyError) {
            if (searchResultStatus !== SEARCH_STATUS.SUCCESS) {
                setSearchHasError(true);
                setSearchErrorMessage(costAndRevErrorMessage.SIMPLE_ERROR);
            } else {
                setSearchHasError(false);
                setSearchErrorMessage('');
                setHasError(false);
                setErrorMessage('');
            }
        }
    }, [errorList, searchResultStatus, isOthers, isAdding]);

    useEffect(() => {
        if (athenaBillToParty && athenaBillToParty.beCode && athenaBillToParty.factCode && athenaBillToParty.name) {
            setOthers(true);
        }
    }, [athenaBillToParty]);

    useEffect(() => {
        if (!isAdding && billToParty) {
            onBillToPartyDropDownChange(billToParty);
        }
    }, []);

    const getPartyDetailsParts = (details: TypePartyDetails): string[] => {
        switch (details.type) {
            case 'partner':
                return [details.country, details.beCode, details.scV_SCACCode, details.name];
            case 'customer':
                return [details.customerCode.substring(0, 2), details.becode, details.scvCode, details.customerName];
            case 'other':
                return [details.countryCode, details.beCode, details.scvNumber, details.name];
            default:
                return [];
        }
    };

    const updatePartyDetails = (details?: any) => {
        if (!details) {
            onUpdate(FIELD_NAMES.PARTNER_DETAILS, '');
            return;
        }
        const parts = getPartyDetailsParts(details);
        const partyDetails = parts.join('|');
        onUpdate(FIELD_NAMES.PARTNER_DETAILS, partyDetails);
    };

    const getPartyInfo = (value: string) => {
        const partner = partnerMap.get(value);
        const customer = customerMap.get(value);
        if (partner && customer) {
            return { type: 'partner', ...partner, interCompanyIC: customer.interCompanyIC };
        }
        if (partner) { return { type: 'partner', ...partner }; }
        if (customer) { return { type: 'customer', ...customer }; }
        return null;
    };

    const updateBillToPartyValues = (partyCode: string, partyName: string, icbIndicator: string) => {
        const updateOrder = billToPartyError
            ? [FIELD_NAMES.ICB, FIELD_NAMES.PARTY_NAME, FIELD_NAMES.PARTY_CODE]
            : icbIndicatorError
                ? [FIELD_NAMES.PARTY_NAME, FIELD_NAMES.PARTY_CODE, FIELD_NAMES.ICB]
                : [FIELD_NAMES.ICB, FIELD_NAMES.PARTY_NAME, FIELD_NAMES.PARTY_CODE];

        updateOrder.forEach((field) => {
            const value = field === FIELD_NAMES.PARTY_CODE ? partyCode : field === FIELD_NAMES.PARTY_NAME ? partyName : icbIndicator;
            onUpdate(field, value);
        });
    };

    const onBillToPartyDropDownChange = useCallback((value: string) => {
        setHasError(false);
        setErrorMessage('');
        if (value === '') {
            setSearchBillToPartyResult('');
            setSearchHasError(false);
            setSearchErrorMessage('');
            updatePartyDetails();
            updateBillToPartyValues('', '', '-');
            setOthers(true);
        } else {
            const partyInfo = getPartyInfo(value);
            if (partyInfo) {
                const { type, name, customerName, interCompanyIC } = partyInfo;
                const icbIndicator = interCompanyIC || '-';
                const partyName = type === PARTNER_TYPES.PARTNER ? name : customerName;
                updatePartyDetails(partyInfo);
                updateBillToPartyValues(value, partyName, icbIndicator);
            }
        }
    }, [
        setHasError,
        setErrorMessage,
        setSearchBillToPartyResult,
        setSearchHasError,
        setSearchErrorMessage,
        updatePartyDetails,
        updateBillToPartyValues,
        setOthers,
        getPartyInfo
    ]);

    const handleSearchBillToPartyInputChange = (value: string) => {
        setSearchBillToPartyResult(value);
    };

    const handleSearchBillToParty = async (value: string) => {
        try {
            setSearchHasError(false);
            setSearchErrorMessage('');
            setSearchResultsStatus(SEARCH_STATUS.PENDING);

            const response = await searchBillToPartyAction({ beCode: value });

            if (!response?.beCode || !response?.factCode || !response?.name) {
                setSearchResultsStatus(SEARCH_STATUS.NO_DATA);
                setSearchHasError(true);
                setSearchErrorMessage('No data found');
                setSearchBillToPartyResult('');
                return;
            }

            setSearchResultsStatus(SEARCH_STATUS.SUCCESS);
            setSearchBillToPartyResult(`${response.beCode} - ${response.factCode} - ${response.name}`);
            updateBillToPartyValues(response.factCode, response.name, '-');
            updatePartyDetails({ type: 'other', ...response });
        } catch (error) {
            setSearchResultsStatus(SEARCH_STATUS.FAILED);
            setSearchHasError(true);
            setSearchErrorMessage(SEARCH_STATUS.API_ERROR);
        }
    };

    const handleInputKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        setSearchHasError(false);
        setSearchErrorMessage('');
    };

    const handleSetErrorMessage = (error: string) => {
        setSearchHasError(true);
        setSearchErrorMessage(error);
    };

    const handleAthenaBillToParty = () => {
        const { beCode, factCode, name, countryCode, scvNumber } = athenaBillToParty;
        const searchResult = `${beCode} - ${factCode} - ${name}`;
        updatePartyDetails({ type: 'other', countryCode, beCode, scvNumber, name });
        updateBillToPartyValues(factCode, name, '-');
        setSearchBillToPartyResult(searchResult);
    };

    const onCloseOthersSearch = () => {
        updatePartyDetails();
        updateBillToPartyValues('', '', '-');
        setSearchBillToPartyResult('');
        setSearchResultsStatus(SEARCH_STATUS.FAILED);
        setHasError(false);
        setErrorMessage('');
        setOthers(false);
    };

    const getDropdownOptions = (partnersOptions: IOptions[], customersOptions: IOptions[]) => {
        return [...(partnersOptions || []), OTHER_DROPDOWN_OPTION, ...(customersOptions || [])];
    };

    return (
        <React.Fragment>
            {!isOthers &&
                <BillToPartySelect
                    billToParty={billToParty}
                    optionsData={
                        getDropdownOptions(billToPartners, billToCustomers)
                    }
                    onBillToPartyDropDownChange={onBillToPartyDropDownChange}
                    hasError={hasError}
                    errorMsg={errorMessage}
                    isDisabled={isDisabled}
                    athenaBillToParty={athenaBillToParty}
                    showLabel={showLabel}
                />
            }
            {isOthers &&
                <BillToPartyInput
                    billToCustomers={billToCustomers}
                    billToPartySearchResponse={searchBillToPartyResult}
                    searchHasError={searchHasError}
                    searchErrorMessage={searchErrorMessage}
                    isDisabled={isDisabled}
                    athenaBillToParty={athenaBillToParty}
                    onInputChange={handleSearchBillToPartyInputChange}
                    onSearchBillToParty={handleSearchBillToParty}
                    onInputKeyUp={handleInputKeyUp}
                    onSetErrorMessage={handleSetErrorMessage}
                    updateAthenaBillToParty={handleAthenaBillToParty}
                    onBillToPartyDropDownChange={onBillToPartyDropDownChange}
                    closeOthersSearch={onCloseOthersSearch}
                />
            }
        </React.Fragment>
    );
};

const mapStateToProps = (state: any) => ({
    billToPartners: state.billToParty.partners,
    billToPartnersRawData: state.billToParty.partnersRawData,
    billToCustomers: state.billToParty.customers,
    billToCustomersRawData: state.billToParty.customersRawData,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>): IMapDispatchToProps => ({
    searchBillToPartyAction: (code: { beCode?: string; factCode?: string; }) => dispatch(checkVendor(code)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BillToPartyComponent);